<template>
  <div id="settlementAuditNew">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="title">
        <i class="dot"></i>
        供应商结算审核
      </div>
      <el-tag :type="statusTagType" size="medium" effect="dark">
        {{ statusText }}
      </el-tag>
    </div>

    <!-- 结算单信息（全部只读） -->
    <el-form
      v-if="slForm"
      ref="slForm"
      :model="slForm"
      label-position="top"
      v-loading="loading"
      :element-loading-text="loadingText"
      class="audit-form"
    >
      <!-- 基本信息 -->
      <div class="form-section">
        <div class="section-title">基本信息</div>
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item label="收款方式">
              <el-select v-model="slForm.collectWay" disabled>
                <el-option v-for="item in collectWayEnums" :key="item.value" :label="item.label" :value="item.value"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="费用类型">
              <el-select v-model="slForm.feeType" disabled>
                <el-option value="0" label="会议类"></el-option>
                <el-option value="1" label="非会议类"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="项目名称">
              <el-input v-model="slForm.projectName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="供应商名称">
              <el-input v-model="slForm.supplierName" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="本次收款金额">
              <el-input v-model="slForm.applyMoney" disabled>
                <template slot="append">元</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="金蝶费用类型">
              <el-select v-model="slForm.feeTypeNo" disabled filterable>
                <el-option v-for="item in expenseItem" :key="item.expenseItemID" :label="item.expenseItem" :value="item.expenseItemID"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="金蝶项目编号">
              <el-input v-model="slForm.projectNo" disabled></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="发票类型">
              <el-select v-model="slForm.receiptType" disabled>
                <el-option value="0" label="电子发票"></el-option>
                <el-option value="1" label="纸质发票"></el-option>
                <el-option value="2" label="电子+纸质发票"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="快递单号">
              <el-input v-model="slForm.deliveryNum" disabled></el-input>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 会议场次 -->
        <el-row>
          <el-col :span="24">
            <el-form-item label="本次结算的会议场次">
              <el-select v-model="slForm.meetingIds" multiple :collapse-tags="false" disabled>
                <el-option v-for="item in meetingList" :key="item.id" :label="item.meetingTitle" :value="Number(item.id)">
                  {{ item.meetingTitle }}
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </div>

      <!-- 支出明细（只读） -->
      <div class="form-section">
        <div class="section-title">支出明细</div>
        <el-table :data="slForm.spendingDetailDtos" border size="small">
          <el-table-column label="序号" type="index" width="60" align="center"></el-table-column>
          <el-table-column label="支出明细" align="center">
            <template slot-scope="scope">
              <span>{{ getSpendingLabel(scope.row.spendingProjectCode) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="金额（元）" width="200" align="center">
            <template slot-scope="scope">{{ scope.row.money }} 元</template>
          </el-table-column>
        </el-table>
        <div class="total-bar">
          合计本次结算金额：<b>{{ slForm.applyMoney || 0 }}</b> 元
        </div>
      </div>

      <!-- ========== 材料审核区域 ========== -->
      <div class="form-section materials-section">
        <div class="section-title">
          <i class="el-icon-folder-checked"></i>
          结算材料审核
          <span class="material-tip">（点击展开查看附件详情）</span>
        </div>

        <fee-slot-panel
          v-for="cat in activeCategories"
          :key="cat.code"
          :category="cat"
          mode="view"
          :can-edit="false"
          :files-map="getCategoryFiles(cat.code)"
          :audit-results="getCategoryAuditResults(cat.code)"
          :conditions="meetingConditions"
          :default-expanded="cat.defaultExpanded"
          @file-preview="onFilePreview"
        />
      </div>

      <!-- ========== AI 智能审核面板 ========== -->
      <!-- 注: 不放在这里了,下面 el-form 后面单独放 -->

      <!-- 审核操作按钮 -->
      <div class="form-actions" v-if="slForm.status === 'OFFICIAL_EXAMINE' || slForm.status === 'SUBMIT'">
        <el-button type="success" size="medium" icon="el-icon-check" @click="openAuditDialog('SUCCESS')">
          审核通过
        </el-button>
        <el-button type="danger" size="medium" icon="el-icon-close" @click="openAuditDialog('ERROR')">
          审核退回
        </el-button>
      </div>
    </el-form>

    <!-- ========== AI 智能审核面板(独立渲染,不依赖老接口加载) ========== -->
    <ai-audit-panel v-if="orderId" :order-id="orderId" :default-expanded="true" />

    <!-- 审核弹窗 -->
    <el-dialog
      :title="dialogType === 'SUCCESS' ? '审核通过确认' : '审核退回'"
      :visible.sync="auditDialogVisible"
      width="500px"
      append-to-body
    >
      <el-form :model="auditForm" ref="auditForm" :rules="auditRules">
        <el-form-item
          v-if="dialogType === 'SUCCESS'"
          label="审核备注"
          prop="remark"
        >
          <el-input
            type="textarea"
            :rows="4"
            maxlength="100"
            show-word-limit
            placeholder="请输入审核备注"
            v-model="auditForm.remark"
          ></el-input>
        </el-form-item>
        <el-form-item
          v-if="dialogType === 'ERROR'"
          label="退回原因"
          prop="errorMessage"
        >
          <el-input
            type="textarea"
            :rows="4"
            maxlength="100"
            show-word-limit
            placeholder="请输入退回原因"
            v-model="auditForm.errorMessage"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="auditDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="confirmAudit">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 文件预览弹窗 -->
    <el-dialog
      v-if="previewVisible"
      :visible.sync="previewVisible"
      title="文件预览"
      width="80%"
    >
      <img v-if="isImageUrl(previewUrl)" :src="previewUrl" style="width:100%">
      <iframe v-else :src="previewUrl" style="width:100%;height:80vh;border:none"></iframe>
    </el-dialog>
  </div>
</template>

<script>
import FeeSlotPanel from '@/components/FeeSlotPanel.vue'
import AiAuditPanel from '@/components/AiAuditPanel.vue'
import {
  getActiveCategories,
  getOptionalCategories,
  LEGACY_CODE_MAP,
} from '@/config/fee-slot-config.js'
import {
  examineSpendingOrder,
  getOrderByIdWithOfficial,
  orderMeetingListBOfficial,
  officialGetExpenseItem,
} from '@/request/api'

export default {
  name: 'SettlementAuditNew',
  components: { FeeSlotPanel, AiAuditPanel },
  data() {
    return {
      loading: false,
      loadingText: '',
      slForm: null,
      orderId: null,
      collectWayEnums: [
        { value: '0', label: '一次性收款并结项' },
        { value: '1', label: '首次收款' },
        { value: '2', label: '阶段收款' },
        { value: '3', label: '尾款' },
        { value: '4', label: '据实结算' },
      ],
      meetingList: [],
      expenseItem: [],
      // 模拟的审核结果（后续接后端API）
      auditResults: {},
      // 材料文件（从后端加载）
      materialFiles: {},
      // 审核弹窗
      auditDialogVisible: false,
      dialogType: '',
      auditForm: { remark: '', errorMessage: '' },
      auditRules: {
        remark: [
          { required: true, message: '请输入备注', trigger: 'blur' },
          { min: 5, message: '备注不能少于5个字符', trigger: 'blur' },
        ],
        errorMessage: [
          { required: true, message: '请输入退回原因', trigger: 'blur' },
          { min: 5, message: '原因不能少于5个字符', trigger: 'blur' },
        ],
      },
      previewVisible: false,
      previewUrl: '',
    }
  },
  computed: {
    statusTagType() {
      const map = { OFFICIAL_EXAMINE: 'warning', SUBMIT: 'warning', ERROR: 'danger', SUCCESS: 'success' }
      return map[this.slForm?.status] || 'info'
    },
    statusText() {
      const map = {
        CREATED: '已创建', WAIT: '待提交', ERROR: '审核驳回',
        SUBMIT: '待审核', OFFICIAL_EXAMINE: '待审核',
        SUCCESS: '审核通过', FINISH: '已结项',
      }
      return map[this.slForm?.status] || this.slForm?.status || ''
    },
    selectedFeeCodes() {
      if (!this.slForm) return []
      return (this.slForm.spendingDetailDtos || [])
        .map(row => LEGACY_CODE_MAP[row.spendingProjectCode])
        .filter(Boolean)
    },
    activeCategories() {
      return getActiveCategories(this.selectedFeeCodes)
    },
    meetingConditions() {
      return { meetingMode: 'OFFLINE' }
    },
  },
  created() {
    this.orderId = this.$route.query.orderId
    this.loadFormData()
    this.loadExpenseItems()
  },
  methods: {
    async loadFormData() {
      try {
        const res = await getOrderByIdWithOfficial({ orderId: this.orderId })
        if (res.code === 200) {
          const data = JSON.parse(JSON.stringify(res.data))
          for (let k in data) { if (data[k] === null) data[k] = '' }
          if (!data.meetingIds) data.meetingIds = []
          if (!data.spendingDetailDtos) data.spendingDetailDtos = []
          this.slForm = data
          this.loadMeetingList()
        }
      } catch (e) { console.error(e) }
    },
    async loadMeetingList() {
      try {
        const res = await orderMeetingListBOfficial({
          orderId: this.orderId || this.slForm?.id,
        })
        if (res.code === 200) this.meetingList = res.data || []
      } catch (e) { console.error(e) }
    },
    async loadExpenseItems() {
      try {
        const res = await officialGetExpenseItem({ current: 1, size: 3000 })
        if (res.code === 200) this.expenseItem = res.data?.data || []
      } catch (e) { console.error(e) }
    },
    getSpendingLabel(code) {
      const newCode = LEGACY_CODE_MAP[code]
      if (newCode) {
        const cats = getOptionalCategories()
        const found = cats.find(c => c.code === newCode)
        if (found) return found.label
      }
      return code || '未知'
    },
    getCategoryFiles(code) {
      return this.materialFiles[code] || {}
    },
    getCategoryAuditResults(code) {
      return this.auditResults[code] || {}
    },
    onFilePreview(url) {
      if (typeof url === 'object') url = url.url || url
      this.previewUrl = url
      this.previewVisible = true
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|bmp|gif)/i.test(url)
    },

    // ====== 审核操作 ======
    openAuditDialog(type) {
      this.dialogType = type
      this.auditDialogVisible = true
      this.auditForm = { remark: '', errorMessage: '' }
    },
    confirmAudit() {
      this.$refs.auditForm.validate(async valid => {
        if (!valid) return
        this.auditDialogVisible = false
        this.loading = true
        this.loadingText = '正在提交...'
        try {
          const res = await examineSpendingOrder({
            orderId: this.slForm.id,
            status: this.dialogType,
            remark: this.auditForm.remark,
            errorMessage: this.auditForm.errorMessage,
          })
          if (res.code === 200) {
            this.$message.success('操作成功')
            this.loadFormData()
          } else {
            this.$message.error(res.msg || '操作失败')
          }
        } catch (e) {
          this.$message.error('操作异常')
        } finally {
          this.loading = false
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
#settlementAuditNew {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

  .title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
    position: relative;
    padding-left: 24px;

    .dot {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: #3563e9;
      border: 4px solid #c2d0f8;
    }
  }
}

.audit-form {
  .form-section {
    background: #fff;
    border-radius: 10px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);

    .section-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      align-items: center;
      gap: 8px;

      .material-tip {
        font-size: 12px;
        color: #909399;
        font-weight: normal;
      }
    }
  }

  .el-select {
    width: 100%;
  }
}

.total-bar {
  text-align: right;
  padding: 8px 0;
  font-size: 14px;
  color: #606266;

  b {
    color: #e6a23c;
    font-size: 16px;
  }
}

.materials-section {
  .section-title i {
    color: #67c23a;
  }
}

.form-actions {
  background: #fff;
  border-radius: 10px;
  padding: 16px 20px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: center;
  gap: 16px;
}
</style>
